import React, { FC, useEffect, useState } from 'react'

import { Alert, ClickAwayListener, Snackbar, Typography } from '@mui/material'
import { AlertCTO } from '@shared/AlertCTO'
import { AlertLevelEnum } from '@shared/Enums'
import { dateToEthDate } from '@shared/SchemaTypes'
import { OtStoreState, useOtSelector, useOtDispatch } from '@react/redux/store'
import { getAlertActivityDescription, MESSAGE_OVERRIDES } from './utils/alertMessages'
import { logOT } from '@shared/loggingShared'
import { alertActions } from '@react/redux/alertSlice'

interface AlertWrapperProps {
  children: React.ReactNode
}

export const AlertWrapper: FC<AlertWrapperProps> = ({ children }) => {
  const dispatch = useOtDispatch()
  const [errorTitle, setErrorTitle] = useState('')
  const [errorDescription, setErrorDescription] = useState('')
  const [successTitle, setSuccessTitle] = useState('')
  const [successDescription, setSuccessDescription] = useState('')

  const [refreshCounter, setRefreshCounter] = useState(0)
  const alerts = useOtSelector((state: OtStoreState) => state.alertState.alerts)
  const errorAlerts = alerts.filter((alert: AlertCTO) => alert.alertLevel === AlertLevelEnum.ERROR)
  const successAlerts = alerts.filter((alert: AlertCTO) => alert.alertLevel === AlertLevelEnum.SUCCESS)

  const handleErrorClose = (): void => {
    setErrorTitle('')
    setErrorDescription('')
    setRefreshCounter(refreshCounter + 1)
    // Clear error alerts from Redux store
    dispatch(alertActions.clearAlerts())
  }
  const handleSuccessClose = (): void => {
    setSuccessTitle('')
    setSuccessDescription('')
    setRefreshCounter(refreshCounter + 1)
    // Clear success alerts from Redux store
    dispatch(alertActions.clearAlerts())
  }

  useEffect(() => {
    try {
      const delayTime = dateToEthDate(new Date()) - 10
      const currentAlerts = errorAlerts?.filter((alert: AlertCTO) => alert.createdAt > delayTime)

      if (!currentAlerts || currentAlerts.length === 0) return
      const errorReason = currentAlerts[0].title

      if (errorReason === '') {
        setErrorTitle(currentAlerts[0].title)
        setErrorDescription(currentAlerts[0].description)
        return
      }
      const errorActivityDescritpion = getAlertActivityDescription(currentAlerts[0].activity ?? 'none')

      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      setErrorTitle(errorActivityDescritpion + (MESSAGE_OVERRIDES[errorReason] ?? errorReason))
      setErrorDescription(currentAlerts[0].description)
    } catch (error) {
      logOT.info('error ', error)
    }
  }, [errorAlerts, refreshCounter])

  useEffect(() => {
    try {
      const delayTime = dateToEthDate(new Date()) - 6

      const currentAlerts = successAlerts?.filter((alert: AlertCTO) => alert.createdAt > delayTime)

      if (!currentAlerts || currentAlerts.length === 0) return
      setSuccessTitle(currentAlerts[0].title)
      setSuccessDescription(currentAlerts[0].description)
    } catch (error) {
      logOT.info('error ', error)
    }
  }, [successAlerts, refreshCounter])

  return (
    <>
      <ClickAwayListener onClickAway={handleErrorClose}>
        <Snackbar
          open={errorTitle.length > 0}
          autoHideDuration={6000}
          onClose={handleErrorClose}
          sx={{ maxWidth: '80vw' }}
        >
          <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
            <div>
              <Typography variant="bodyBold">{errorTitle}</Typography>
            </div>
            {errorDescription !== '' && (
              <div>
                <Typography variant="bodyMuted">{errorDescription}</Typography>
              </div>
            )}
          </Alert>
        </Snackbar>
      </ClickAwayListener>
      <ClickAwayListener onClickAway={handleSuccessClose}>
        <Snackbar
          open={successTitle.length > 0}
          autoHideDuration={6000}
          onClose={handleSuccessClose}
          sx={{ maxWidth: '80vw' }}
        >
          <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
            <div>
              <Typography variant="bodyBold">{successTitle}</Typography>
            </div>
            {successDescription !== '' && (
              <div>
                <Typography variant="bodyMuted">{successDescription}</Typography>
              </div>
            )}
          </Alert>
        </Snackbar>
      </ClickAwayListener>
      {children}
    </>
  )
}
